// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode, type RefObject } from "react";
import { animate, useMotionValue, type AnimationPlaybackControls, type MotionValue } from "motion/react";
import { matchesMedia, REDUCED_MOTION_QUERY } from "@/components/wingo/hooks/use-media-query";
import { springs } from "@/components/wingo/lib/motion";

// Wingo UI Pro · https://wingo-ui.com/legal/license

// ---------------------------------------------------------------- scroll container context

// the element that scrolls for everything inside it. an app shell whose content area scrolls (instead of the window),
// a demo box or a phone frame provides it, so top bars, tab bars, back-to-top buttons and scroll spies inside follow
// that element without a prop. null means the window
const ScrollContainerContext = createContext<HTMLElement | null>(null);

export type ScrollContainerProviderProps = { value: HTMLElement | null; children?: ReactNode };

// keep the element in state (a callback ref that calls setState), so the children update once it mounts
export function ScrollContainerProvider({ value, children }: ScrollContainerProviderProps) {
  return createElement(ScrollContainerContext.Provider, { value }, children);
}

// the nearest provided scroll container, or null for the window
export function useScrollContainer(): HTMLElement | null {
  return useContext(ScrollContainerContext);
}

// the nearest ancestor that scrolls vertically, or null when the window does. safe outside react
export function findScrollParent(element: Element | null): HTMLElement | null {
  let node = element?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

// ---------------------------------------------------------------- scrolling

export type ScrollTarget = HTMLElement | Window | RefObject<HTMLElement | null> | null | undefined;

type Scroller = HTMLElement | Window;

// what a target option points at right now; refs are read here, so call it from effects and handlers only
function resolveScroller(target: ScrollTarget, provided: HTMLElement | null): Scroller | null {
  if (typeof window === "undefined") return null;
  if (target === undefined) return provided ?? window;
  if (target === null) return window;
  if ("current" in target) return target.current ?? window;
  return target;
}

function isWindow(target: Scroller): target is Window {
  return typeof window !== "undefined" && target === window;
}

function metrics(target: Scroller) {
  if (isWindow(target)) {
    const doc = document.documentElement;
    return { top: window.scrollY, max: Math.max(0, doc.scrollHeight - window.innerHeight) };
  }
  return { top: target.scrollTop, max: Math.max(0, target.scrollHeight - target.clientHeight) };
}

// scrolls a target (default: the window) to a position. smooth unless the user asked for reduced motion
export function scrollToOffset(target: HTMLElement | Window | null | undefined, top: number, smooth = true) {
  if (typeof window === "undefined") return;
  const behavior: ScrollBehavior = smooth && !matchesMedia(REDUCED_MOTION_QUERY) ? "smooth" : "auto";
  (target ?? window).scrollTo({ top, behavior });
}

export type ScrollDirection = "up" | "down";

export type UseScrollStateOptions = {
  // what to watch: an element, a ref to one, or the window. default: the nearest ScrollContainerProvider, else the window
  target?: ScrollTarget;
  // `scrolled` turns true past this many px (a header hairline, a back-to-top button). default 1
  scrolledThreshold?: number;
  // track hideProgress / hidden for bars that slide away while you scroll down. default false (no work when unused)
  hideOnScroll?: boolean;
  // px of scrolling that takes a bar from shown to hidden (usually the bar's height). default 56
  hideDistance?: number;
  // a bar never hides while the offset is below this (keep a header until the page header has scrolled away). default 0
  hideOffset?: number;
  // bring the bar back at the very end of the content, where nothing is left to reveal. default true
  revealAtEnd?: boolean;
  // after scrolling stops, settle a half-hidden bar to shown or hidden on a snappy spring. default true
  snap?: boolean;
  disabled?: boolean;
};

export type ScrollState = {
  // the scroll offset in px, updated every frame without re-rendering (negative during an ios overscroll bounce)
  offset: MotionValue<number>;
  // 0 at the top, 1 at the bottom of the scrollable distance
  progress: MotionValue<number>;
  // 0 shown to 1 hidden: follows the finger 1:1 while scrolling (down hides, up reveals), then snaps. drive a bar with
  // useTransform(hideProgress, [0, 1], [0, -height]). stays 0 unless hideOnScroll is on
  hideProgress: MotionValue<number>;
  // the following values re-render only when they flip, never per frame
  direction: ScrollDirection | null;
  scrolled: boolean;
  // hideProgress is past one half
  hidden: boolean;
  // at the bottom of the content
  atEnd: boolean;
  // scrolls the watched target, smooth unless reduced motion
  scrollTo: (top: number, options?: { smooth?: boolean }) => void;
};

const IDLE_MS = 140;

// scroll position, progress and hide-on-scroll state for the window or a container, SSR-safe: the server and the
// first render report the top of the page. position values are motion values, so a bar can move with the scroll at
// 60fps without a react render; booleans re-render only when they change
export function useScrollState({
  target,
  scrolledThreshold = 1,
  hideOnScroll = false,
  hideDistance = 56,
  hideOffset = 0,
  revealAtEnd = true,
  snap = true,
  disabled = false,
}: UseScrollStateOptions = {}): ScrollState {
  const provided = useScrollContainer();
  const offset = useMotionValue(0);
  const progress = useMotionValue(0);
  const hideProgress = useMotionValue(0);
  const [flags, setFlags] = useState({ direction: null as ScrollDirection | null, scrolled: false, hidden: false, atEnd: false });

  useEffect(() => {
    const scroller = resolveScroller(target, provided);
    if (!scroller || disabled) return;
    const distance = Math.max(1, hideDistance);
    let last = metrics(scroller).top;
    let idle = 0;
    let settle: AnimationPlaybackControls | null = null;
    // where the bar is heading, 0 to 1. equals hideProgress except while a snap animation runs, and under reduced
    // motion, where hideProgress only ever shows 0 or 1 so nothing slides
    let acc = hideProgress.get();
    let state = { direction: null as ScrollDirection | null, scrolled: false, hidden: acc >= 0.5, atEnd: false };

    const commit = (patch: Partial<typeof state>) => {
      const next = { ...state, ...patch };
      if (next.direction === state.direction && next.scrolled === state.scrolled && next.hidden === state.hidden && next.atEnd === state.atEnd) return;
      state = next;
      setFlags(next);
    };

    const setHide = (value: number, animated: boolean) => {
      settle?.stop();
      settle = null;
      acc = value;
      if (animated && !matchesMedia(REDUCED_MOTION_QUERY)) settle = animate(hideProgress, value, springs.snappy);
      else hideProgress.set(value);
      commit({ hidden: value >= 0.5 });
    };

    const measure = () => {
      const { top, max } = metrics(scroller);
      const delta = top - last;
      last = top;
      offset.set(top);
      progress.set(max > 0 ? Math.min(1, Math.max(0, top / max)) : 0);
      const atEnd = max > 0 && max - top <= 1;
      commit({
        scrolled: top > scrolledThreshold,
        atEnd,
        direction: delta > 0 ? "down" : delta < 0 ? "up" : state.direction,
      });

      if (!hideOnScroll) return;
      if (top <= 0) {
        if (acc !== 0) setHide(0, false);
        return;
      }
      if (revealAtEnd && atEnd) {
        if (acc !== 0) setHide(0, true);
        return;
      }
      if (delta !== 0) {
        if (settle) {
          // the finger took over from a snap in progress: continue from where the bar is
          settle.stop();
          settle = null;
          acc = hideProgress.get();
        }
        // above hideOffset a bar only ever comes back; past it, it follows the finger both ways
        acc = top <= hideOffset && delta > 0 ? acc : Math.min(1, Math.max(0, acc + delta / distance));
        if (matchesMedia(REDUCED_MOTION_QUERY)) {
          // no sliding: flip once the finger has moved a full bar height in one direction
          if ((acc === 1 || acc === 0) && hideProgress.get() !== acc) setHide(acc, false);
        } else {
          hideProgress.set(acc);
          commit({ hidden: acc >= 0.5 });
        }
      }
      window.clearTimeout(idle);
      if (snap && !matchesMedia(REDUCED_MOTION_QUERY)) {
        idle = window.setTimeout(() => {
          if (acc > 0 && acc < 1) setHide(acc >= 0.5 && metrics(scroller).top > hideOffset ? 1 : 0, true);
        }, IDLE_MS);
      }
    };

    measure();
    const onScroll = () => measure();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    // content that grows or shrinks changes progress and the end without a scroll event
    const resize = new ResizeObserver(() => measure());
    if (isWindow(scroller)) resize.observe(document.documentElement);
    else {
      resize.observe(scroller);
      if (scroller.firstElementChild) resize.observe(scroller.firstElementChild);
    }
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      resize.disconnect();
      window.clearTimeout(idle);
      settle?.stop();
    };
  }, [target, provided, disabled, hideOnScroll, hideDistance, hideOffset, revealAtEnd, snap, scrolledThreshold, offset, progress, hideProgress]);

  const scrollTo = useCallback(
    (top: number, options?: { smooth?: boolean }) => scrollToOffset(resolveScroller(target, provided), top, options?.smooth ?? true),
    [target, provided],
  );

  return useMemo(() => ({ offset, progress, hideProgress, ...flags, scrollTo }), [offset, progress, hideProgress, flags, scrollTo]);
}
